import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLogAnalyticsEvent } from 'shared/src/hooks';
import { getNumberRange, isNotNil } from 'shared/src/utils';
import type { LogAnalyticsEventParams } from 'shared/src/utils';
import type { ILogging } from 'shared/src/api/types/ILogging';
import type { MarketDetail } from 'shared/src/api';
import { queryKey } from '../../../constant/queryKey';

export const useGetMarketAnalyticsDefaults = (
  marketId: number,
): (() => ILogging | undefined) => {
  const queryClient = useQueryClient();

  return useCallback(() => {
    const detail = queryClient.getQueryData<MarketDetail>(
      queryKey.marketDetail(marketId),
    );
    if (!detail) return undefined;

    const isExistDownloadableCoupon =
      detail.market.is_exist_usable_coupon &&
      isNotNil(detail.max_price_coupon);

    const isTodayMarket = detail.market.approved_delivery_types.some(
      item => item.value === 3,
    );

    return {
      analytics: {
        MARKET_NAME: detail.market.name,
        MARKET_SNO: detail.market.sno.toString(),
        FAVORITE_COUNT: detail.market.favorites_count,
        FAVORITE_COUNT_RANGE: getNumberRange({
          rangeUnit: 500,
          target: detail.market.favorites_count,
        }),
        REVIEW_RATING: Math.floor(
          detail.market.purchasing_satisfaction_ratio,
        ),
        MARKET_TYPE: detail.market.market_type.name,
        AGE_TAG1: detail.market.age_tags[0],
        AGE_TAG2: detail.market.age_tags[1],
        STYLE_TAG1: detail.market.style_tags[0],
        STYLE_TAG2: detail.market.style_tags[1],
        IS_EXIST_USABLE_COUPONS: detail.market.is_exist_usable_coupon ? 1 : 0,
        IS_EXIST_DOWNLOADABLE_COUPONS: isExistDownloadableCoupon ? 1 : 0,
        IS_TODAY_MARKET: isTodayMarket ? 1 : 0,
      },
    };
  }, [queryClient, marketId]);
};

export const useLogMarketAnalyticsEvent = () => {
  const logAnalyticsEvent = useLogAnalyticsEvent();

  return useCallback(
    (params: LogAnalyticsEventParams) => {
      logAnalyticsEvent({
        ...params,
        channel: params.channel ?? [
          'firebase',
          'facebook',
          'amplitude',
          'braze',
          'ablylog',
        ],
      });
    },
    [logAnalyticsEvent],
  );
};
